import {
  type ComboItemProps,
  KdsButton,
  KdsDatePicker,
  KdsDivider,
  KdsDropfile,
  KdsMultiCombobox,
} from "@kazaar-platform/design-system";
import { RiSubtractLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { isAxiosError } from "axios";
import { useAtomValue } from "jotai";
import { useContext, useState } from "react";
import { Controller } from "react-hook-form";
import { FileContext } from "@/components/FileUploadAsync/FileUploadAsync.context";
import { SIZE_5G } from "@/constants/fileSize.constants";
import {
  getDatePickerErrorMessage,
  getDropfileErrorMessage,
} from "@/helpers/forms";
import { getUserLocale } from "@/helpers/lang";
import { useDirectUpload } from "@/hooks/useDirectUpload";
import { userAtom } from "@/store";
import { type DeliveryNoteProps } from "./DeliveryNote.types";

export const DeliveryNote = ({
  index,
  methods,
  items,
  orderItems,
  remove,
}: DeliveryNoteProps) => {
  const { t } = useTranslate();

  const [isLoading, setIsLoading] = useState(false);

  const { pushFile, setFileUploadState } = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const userAtomValue = useAtomValue(userAtom);

  const handleUpload = async (file: File) => {
    try {
      const uploadDetails = await createFileUploadUrl(
        "orderItem.deliveryNotes",
        file
      );
      const newSignedId = uploadDetails?.fileSignedId;

      pushFile(newSignedId, {
        fileName: file.name,
        size: file.size,
        contentType: file.type,
      });

      await uploadFile(file, uploadDetails);

      setFileUploadState(newSignedId, "uploaded");

      methods.setValue(`deliveryNotes.${index}.fileSignedId`, newSignedId);
    } catch (error) {
      if (isAxiosError(error)) {
        // Check if it's an invalid file type
        if (error.response?.status === 422) {
          setFileUploadState(error.response?.data.fileSignedId, "error");
          methods.setError(`deliveryNotes.${index}.fileSignedId`, {
            type: "manual",
            message: t("cardAddDeliveryNote.deliveryNote.errorDropfileType"),
          });
          methods.setValue(`deliveryNotes.${index}.fileSignedId`, undefined);
          setIsLoading(false);
        }
      }
    }
  };

  return (
    <div className="mb-4 flex flex-col gap-sp-md">
      <Controller
        name={`deliveryNotes.${index}.fileSignedId`}
        render={({ field, fieldState }) => (
          <KdsDropfile
            dataCy="fileUploadForDeliveryNote"
            errorMessage={getDropfileErrorMessage(fieldState.error?.message)}
            hasError={!!fieldState.error}
            isDeletable={!!field.value}
            isLoading={isLoading}
            isReadable={!!field.value}
            maxSize={SIZE_5G}
            onDelete={() => field.onChange(undefined)}
            onUploadError={() =>
              methods.setValue(`deliveryNotes.${index}.fileSignedId`, undefined)
            }
            onUploadStart={() => {
              setIsLoading(true);
              methods.clearErrors(`deliveryNotes.${index}.fileSignedId`);
            }}
            onUpload={(file) => void handleUpload(file)}
            onUploadSuccess={() => setIsLoading(false)}
            placeholderDescription={t(
              "cardAddDeliveryNote.deliveryNote.description"
            )}
            placeholderTitle={t("cardAddDeliveryNote.deliveryNote.title")}
          />
        )}
      />

      <Controller
        name={`deliveryNotes.${index}.deliveryDate`}
        render={({ field, fieldState }) => (
          <KdsDatePicker
            calendarProps={{
              locale: getUserLocale({ locale: userAtomValue?.lang }),
            }}
            dataCy="deliveryDate"
            errorDataCy="deliveryDateError"
            errorMessage={getDatePickerErrorMessage(fieldState.error?.message)}
            hasError={!!fieldState.error}
            label={t("cardAddDeliveryNote.deliveryNote.deliveryDate")}
            onChange={(value) => {
              const date = value?.toDate("utc");
              field.onChange(date);
            }}
            required
          />
        )}
      />

      <Controller
        name={`deliveryNotes.${index}.orderItemIds`}
        render={({ field, fieldState }) => (
          <KdsMultiCombobox
            errorMessage={t("cardAddDeliveryNote.deliveryNote.errorProducts")}
            hasError={!!fieldState.error}
            dataCy="deliveryNoteProducts"
            items={items}
            label={t("cardAddDeliveryNote.deliveryNote.products")}
            required
            selectedItems={field.value?.map((id: string) => {
              const item = orderItems.find((item) => item.id === id);

              return {
                id: item?.id,
                textValue: item?.sandboxItem?.name,
                label: item?.sandboxItem?.name,
              };
            })}
            onSelectedItemsChange={(items: ComboItemProps[] | undefined) =>
              field.onChange(items?.map((item) => item.id) ?? [])
            }
          />
        )}
      />

      {index > 0 && (
        <KdsButton
          color="error"
          iconLeft={RiSubtractLine}
          onPress={() => remove(index)}
          variant="ghost"
        >
          {t("cardAddDeliveryNote.buttons.removeDeliveryNote")}
        </KdsButton>
      )}

      {index < 9 && <KdsDivider />}
    </div>
  );
};
